import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useTranslation } from '../LanguageContext';

/**
 * "You have collaborations left to rate."
 *
 * The rating form itself lives on the deal screen, which is exactly where
 * nobody goes once a deal is finished — the work is done, the money moved,
 * the card gets collapsed and never opened again. A reputation system fed
 * only from there would starve.
 *
 * So the prompt is lifted onto the finance page, which people *do* come back
 * to. It renders nothing when there is nothing outstanding, so it costs a
 * quiet account nothing.
 */

const usd = (value) => `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0, maximumFractionDigits: 0,
})}`;

function PendingRatings({ className = '' }) {
    const { t } = useTranslation();
    const [pending, setPending] = useState([]);

    useEffect(() => {
        let alive = true;
        api.get('/api/reputation/pending/')
            .then(res => { if (alive) setPending(res.data.pending || []); })
            .catch(() => { /* the interceptor surfaces auth errors */ });
        return () => { alive = false; };
    }, []);

    if (!pending.length) return null;

    return (
        <section className={`rounded-2xl border border-amber-200 bg-amber-50/50 p-4 sm:p-5 ${className}`}>
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-amber-500"
                    fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                {t('reputation.pendingTitle')}
                <span className="text-xs text-amber-800 bg-amber-100 px-2.5 py-1 rounded-full font-bold ml-auto border border-amber-200">
                    {pending.length}
                </span>
            </h2>
            <p className="text-xs text-slate-500 mt-1 mb-4 leading-relaxed">
                {t('reputation.pendingHint')}
            </p>

            <div className="space-y-2">
                {pending.map(row => (
                    <div key={row.deal_id}
                        className="flex items-center gap-3 bg-white rounded-xl border border-amber-100 p-3">
                        <img src={row.counterparty.profile_picture || '/logo192.png'} alt=""
                            className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-800 truncate">
                                {row.counterparty.username}
                            </p>
                            <p className="text-[11px] text-slate-400 truncate">
                                {row.campaign} · {usd(row.amount_usd)}
                            </p>
                        </div>
                        {/* The form is on the deal card, so this is a pointer
                            rather than a second place to submit from — two
                            entry points into one record is how they drift. */}
                        <Link to="/campaigns"
                            className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-amber-800 bg-amber-100 hover:bg-amber-200 border border-amber-200 transition flex-shrink-0 no-underline">
                            {t('reputation.rateBtn')}
                        </Link>
                    </div>
                ))}
            </div>
        </section>
    );
}

export default PendingRatings;
